import type { SlotsChain } from "@0xslots/sdk";
import type { AdData } from "@adland/data";
import { adlandApiUrl } from "@adland/data";
import {
  type Address,
  type Chain,
  createPublicClient,
  fallback,
  http,
  keccak256,
  type PublicClient,
  parseAbi,
  stringToHex,
  type Transport,
  toBytes,
} from "viem";
import { base, baseSepolia } from "viem/chains";

import { AdDataQueryError, type AdSnapshot } from "./types";

/**
 * The only two contract reads rendering an ad requires.
 *
 * Declared here as one-value calls rather than through the protocol's own
 * `getSlotInfo()` or the hook's `ad(slot)`. Both return a large struct whose
 * shape moves as the protocol evolves — `SlotInfo` is twenty-eight fields — and
 * a field added to it breaks the tuple this bundle decodes against, from a
 * contract that is working perfectly. Publishers pin this on a CDN and never
 * upgrade it, so a `string` and an `address` are what the render path asks for.
 */

/**
 * Which hook a slot points at — asked of the slot, not assumed.
 *
 * Reading it beats matching against a table of our own addresses: a publisher
 * running their own AdLand deployment is read correctly with nothing passed,
 * and a slot whose hook is something else entirely is correctly read as having
 * no ad rather than being looked up on ours.
 */
const SLOT_HOOK_ABI = parseAbi(["function hook() view returns (address)"]);

/**
 * The hook, read one value at a time.
 *
 * `creativeOf` is the whole render path: the creative lives on the hook, keyed
 * by slot, and it returns "" for a slot the hook does not manage. `slotOf`
 * resolves a registry name, which is what an embed carrying no `slot` falls
 * back to.
 */
const AD_HOOK_ABI = parseAbi([
  "function creativeOf(address slot) view returns (string)",
  "function slotOf(bytes32 key) view returns (address)",
]);

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

/** Default IPFS gateway — econome. Origin only; `/ipfs/<cid>` is appended. */
export const DEFAULT_IPFS_GATEWAY = "https://ipfs-gateway.econome.studio";

/** Build a gateway URL for a CID. */
export function gatewayUrl(cid: string, gateway?: string): string {
  const origin = (gateway ?? DEFAULT_IPFS_GATEWAY).replace(/\/$/, "");
  return `${origin}/ipfs/${cid}`;
}

/** Chains supported out of the box. Publishers can extend this via config. */
export const DEFAULT_CHAINS: Record<number, Chain> = {
  [base.id]: base,
  [baseSepolia.id]: baseSepolia,
};

/**
 * Create a read-only client for a given chain.
 *
 * `chains` defaults to Base + Base Sepolia; pass a wider registry to read slots
 * on other chains without forking the package. This returns a plain viem client
 * — the render path needs RPC only, so it carries no subgraph or SDK
 * configuration and works on any chain a slot is deployed to.
 */
/**
 * Chains adland hosts a read endpoint for. Anywhere else keeps the old default.
 *
 * Kept as its own set rather than derived from `DEFAULT_CHAINS`: a publisher
 * can pass a wider `chains` registry to render slots on other networks, and
 * pointing those at our proxy would send calls it is going to refuse.
 */
const ADLAND_RPC_CHAINS = new Set<number>([base.id, baseSepolia.id]);

/**
 * The transport used when a publisher has not supplied their own RPC.
 *
 * ── What this replaces ───────────────────────────────────────────────────────
 *
 * `http()` with no argument, which resolves to viem's default for the chain —
 * `https://mainnet.base.org` on Base. Base's own documentation says of it:
 * "The public endpoints above are rate-limited and not suitable for production
 * traffic", and publishes no limit, so there was nothing to tune. Every
 * publisher who pasted an embed without owning an RPC key was on it, from their
 * visitors' browsers, at one request per ad per pageview.
 *
 * ── Why both, in this order ──────────────────────────────────────────────────
 *
 * adland's endpoint is tried first: it is cached, so a slot costs the same
 * whether a page is read once or a million times, and it answers from a paid
 * node rather than a shared public one.
 *
 * The public endpoint stays behind it, and that is not belt-and-braces. Before
 * this change an adland outage cost publishers their impression tracking and
 * nothing else — ads still rendered, because the render path never touched our
 * infrastructure. Making our endpoint the only transport would have turned the
 * same outage into every publisher's ad space going blank. A flaky-but-
 * independent dependency traded for a reliable-but-total one is not an upgrade,
 * so `fallback` walks them in order and the second one is what keeps that true.
 *
 * `rank: false` on purpose. Ranking probes every transport on an interval to
 * order them by latency, which would send background traffic to the public
 * endpoint from every page carrying an ad — spending the reliability this
 * exists to buy, to discover something already known.
 */
function defaultTransport(chainId: number): Transport {
  if (!ADLAND_RPC_CHAINS.has(chainId)) return http();
  return fallback([http(`${adlandApiUrl}/rpc/${chainId}`), http()], {
    rank: false,
  });
}

export function createReadClient(
  chainId: SlotsChain,
  rpcUrl?: string,
  chains: Record<number, Chain> = DEFAULT_CHAINS,
): PublicClient {
  const chain = chains[chainId];
  if (!chain) throw new Error(`Unsupported chain: ${chainId}`);

  return createPublicClient({
    chain,
    // An explicit `rpcUrl` still wins outright — a publisher who names one is
    // opting out of our infrastructure, and quietly putting ours in front of
    // theirs would make that opt-out a lie.
    transport: rpcUrl ? http(rpcUrl) : defaultTransport(chainId),
    // Reads issued in the same tick go out as one `aggregate3` instead of one
    // request each.
    //
    // Rendering an ad is not what this is for: its two reads are dependent —
    // the module's address comes out of the first — so they can never share a
    // batch and this changes nothing on that path. It is for the callers that
    // ask a slot several independent questions at once, `fetchSlotTerms` above
    // all, which fires thirteen getters through one `Promise.all` and was
    // paying thirteen round trips for them.
    //
    // Safe on a chain without a multicall3: viem catches
    // `ChainDoesNotSupportContract` and falls back to a plain `eth_call`, which
    // matters because publishers can pass their own `chains` registry.
    batch: { multicall: true },
  }) as PublicClient;
}

/**
 * Extract the IPFS CID from a metadata URI, or null for non-IPFS URIs.
 */
export function extractCid(uri: string): string | null {
  if (uri.startsWith("ipfs://")) return uri.slice(7);
  if (uri.startsWith("Qm") || uri.startsWith("bafy")) return uri;
  return null;
}

/** An ad carried in the URI itself rather than named by it. */
const INLINE = /^data:application\/json(;[^,]*)?,/i;

/**
 * Read an ad out of the URI that carries it.
 *
 * The slot's `tokenURI` is an arbitrary string the module never inspects, so an
 * ad small enough to fit — ours run 245–445 bytes — can travel inside it rather
 * than being named by a CID and fetched from a gateway. That removes the one
 * host on the render path we operate, and a request measured at 135ms.
 *
 * Decoded here rather than handed to `fetch`, which does accept a `data:` URL
 * and was the reason this needed no change at all to work. It is subject to
 * `connect-src` though, and this runs on publishers' pages under content
 * policies we neither see nor control — a blocked fetch would fail on their
 * site and nowhere else. Decoding touches no network stack and cannot be
 * blocked.
 */
function decodeInline(uri: string): string {
  const comma = uri.indexOf(",");
  const body = uri.slice(comma + 1);
  if (!/;base64/i.test(uri.slice(0, comma))) return decodeURIComponent(body);

  const binary =
    typeof atob === "function"
      ? atob(body)
      : Buffer.from(body, "base64").toString("binary");

  // Through bytes, not the binary string `atob` hands back. That string is
  // latin-1, one character per byte, and a cast's text is full of emoji and
  // accents — reading it directly turns every multi-byte character into
  // mojibake and changes the hash below into the bargain.
  return new TextDecoder().decode(
    Uint8Array.from(binary, (c) => c.charCodeAt(0)),
  );
}

/**
 * A stable identity for an inline ad, standing in for the CID it has no longer.
 *
 * Impressions are recorded against the CID — it is how analytics tells one
 * creative from another — and an inline ad has none. Keccak over the bytes as
 * stored gives the same property back: the same creative always hashes the
 * same, and two different ones never collide.
 *
 * Over the decoded string exactly as it came off the chain, never over a
 * re-serialised object: `JSON.stringify` is free to order keys however it likes,
 * so hashing a parsed-then-restringified ad would give one creative two
 * identities depending on who parsed it.
 *
 * `keccak256` rather than `crypto.subtle`, which is synchronous, already a
 * dependency here, and — unlike SubtleCrypto — defined outside a secure context.
 * Some publisher is serving over plain http.
 */
function contentId(json: string): string {
  return keccak256(toBytes(json));
}

/**
 * Fetch ad content from a metadata URI (IPFS or HTTP).
 * Returns both the ad data and the CID (if IPFS).
 *
 * A single gateway attempt is made — there is deliberately no fallback chain.
 */
export const fetchAdFromURI = async (
  uri: string,
  gateway?: string,
): Promise<{ data: AdData; cid: string | null }> => {
  if (!uri) throw new Error(AdDataQueryError.NO_AD);

  // No I/O at all for an inline ad — the bytes are already here.
  if (INLINE.test(uri)) {
    let data: AdData;
    const json = decodeInline(uri);
    try {
      data = JSON.parse(json);
    } catch {
      // Anyone can write anything into a slot they hold, so malformed JSON is a
      // thing that will happen rather than a thing that cannot. It reads as an
      // empty slot, which is the honest rendering of an ad nobody can display.
      throw new Error(AdDataQueryError.NO_AD);
    }
    if (data && typeof data === "object" && "error" in data && data.error) {
      throw new Error(String(data.error));
    }
    return { data, cid: contentId(json) };
  }

  const cid = extractCid(uri);
  const url = cid ? gatewayUrl(cid, gateway) : uri;

  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    if (res.status === 404) throw new Error(AdDataQueryError.NO_AD);
    throw new Error(AdDataQueryError.ERROR);
  }

  const data = await res.json();
  if (data.error) throw new Error(data.error);

  return { data, cid };
};

/**
 * The hook's lens: a name resolved and its creative read, in one call.
 *
 * Only `adByKey` is declared. The slot path has `creativeOf` — one value, no
 * struct — so there is no reason to decode twenty-eight fields to learn a
 * string. Resolving a NAME is the one case where the struct earns its keep,
 * because `slotOf` then `creativeOf` are serial and cannot be batched.
 *
 * `hookFlags` is spelled out because viem needs the shape to walk past it, not
 * because anything here reads it. If this ever fails to decode, the caller
 * falls back to the two single-value calls rather than showing nothing.
 */
const AD_HOOK_LENS_ABI = parseAbi([
  "function adByKey(bytes32 key) view returns ((address slot,string uri,bool managed,(address recipient,address currency,address manager,uint256 taxBps,uint256 minDepositSeconds,bool mutableTax,bool mutableHook,address hook,bytes32 hookData,(bool beforeBuy,bool beforeSelfAssess,bool afterBuy,bool afterRelease,bool afterLiquidate,bool afterSettle,bool strict) hookFlags,address occupant,uint256 price,uint256 deposit,uint64 occupiedSince,uint64 tenureId,uint64 lastSettled,uint256 taxOwed,uint256 collectedTax,bool isVacant,bool isInsolvent,uint256 secondsUntilLiquidation,uint256 pendingTaxBps,address pendingHook,bytes32 pendingHookData,bool pendingHasTax,bool pendingHasHook,uint64 pendingProposedAt,bool hasRipeTerms) info))",
]);

/**
 * The AdLand hook, per chain.
 *
 * The one constant that has to survive: a publisher who stops passing a slot is
 * trusting this address instead. It is a UUPS proxy we control, so it never has
 * to move — which is the whole reason a name can be repointed and an address
 * baked into somebody's HTML cannot.
 *
 * One address on both chains — the deploy is CREATE2 and the initcode is
 * identical — which is a fact about the deployment, not a rule. Written out per
 * chain so a redeploy on one of them can be expressed here.
 */
export const DEFAULT_AD_HOOKS: Record<number, Address> = {
  8453: "0xf93d43532E1cC486479c9cF83CFc6161aAa8F4A9",
  84532: "0xf93d43532E1cC486479c9cF83CFc6161aAa8F4A9",
};

/** A registry name as the contract stores it — right-padded to 32 bytes. */
export function slotKeyToBytes32(key: string): `0x${string}` {
  return stringToHex(key, { size: 32 });
}

/**
 * Read the ad a NAME points at, in a single request.
 *
 * The reason this exists: a publisher pastes `slot="0x…"` into their page, and
 * that address then lives in HTML nobody can reach again. Redeploy the slot and
 * every page carrying it shows a dead space forever. A name is ours to repoint,
 * so the embed stops carrying a decision it cannot revise.
 *
 * One `eth_call` rather than three. The module resolves the name, reads its own
 * `tokenURI` storage for the creative, and returns both — measured at 143ms
 * against Base where the hop-by-hop path was 224 + 130 + 135.
 *
 * The key is derived locally rather than read from the contract's own constant.
 * Asking for it would be a second round trip to learn something that has to be
 * a constant on this side anyway, which is exactly the trip this is removing.
 */
export const fetchAdByKey = async (
  client: PublicClient,
  key: string,
  opts?: { gateway?: string; adHook?: string },
): Promise<(AdSnapshot & { slot: Address }) | null> => {
  const chainId = client.chain?.id ?? -1;
  const hook = opts?.adHook ?? DEFAULT_AD_HOOKS[chainId];

  if (!hook) {
    throw new Error(
      `[@adland/react] No ad hook known for chain ${chainId}. ` +
        `Pass \`adHook\` to resolve a slot by name there.`,
    );
  }

  const view = await readKeyFromHook(client, hook as Address, key);

  // An unset name and a named slot with no creative are both "nothing to
  // draw", and neither is an error — a key can be registered before the slot
  // it points at has ever been sold.
  if (!view || view.slot === ZERO_ADDRESS || !view.uri) return null;

  try {
    const snapshot = await fetchAdFromURI(view.uri, opts?.gateway);
    return { ...snapshot, slot: view.slot };
  } catch (err) {
    if (err instanceof Error && err.message === AdDataQueryError.NO_AD) {
      return null;
    }
    throw err;
  }
};

/**
 * A name resolved against the hook, in one call where the struct allows it.
 *
 * `null` means the name resolves to nothing. An empty creative is NOT null: the
 * name resolved and the slot is simply unsold, which the caller renders the
 * same way but which is a different fact.
 *
 * The fallback is the point of the split. `adByKey` returns the protocol's own
 * `SlotInfo`, and a field added to it changes the tuple this bundle was pinned
 * against — a decode error, from a contract that is working perfectly. Two
 * single-value calls cost one extra round trip and cannot drift, so the ad
 * still renders while somebody works out why the fast path stopped.
 */
async function readKeyFromHook(
  client: PublicClient,
  hook: Address,
  key: string,
): Promise<{ slot: Address; uri: string } | null> {
  const key32 = slotKeyToBytes32(key);
  try {
    const view = await client.readContract({
      address: hook,
      abi: AD_HOOK_LENS_ABI,
      functionName: "adByKey",
      args: [key32],
    });
    return view.slot === ZERO_ADDRESS
      ? null
      : { slot: view.slot, uri: view.uri };
  } catch {
    // Fall through to the calls that cannot decode wrongly.
  }

  try {
    const slot = await client.readContract({
      address: hook,
      abi: AD_HOOK_ABI,
      functionName: "slotOf",
      args: [key32],
    });
    if (slot === ZERO_ADDRESS) return null;

    const uri = await client.readContract({
      address: hook,
      abi: AD_HOOK_ABI,
      functionName: "creativeOf",
      args: [slot],
    });
    return { slot, uri };
  } catch {
    return null;
  }
}

/**
 * A slot's whole creative in one call — the two reads and the gateway fetch.
 *
 * The same three steps `<Ad>` runs, exported so they can be run somewhere the
 * user is not waiting. In a server component the result goes to `initialData`
 * and the ad is in the first paint; in the browser those three steps are
 * strictly serial and cost roughly half a second before the image is given a
 * `src`, all of it after hydration.
 *
 * `null` for a slot with no hook or no ad, which is a real answer and not a
 * failure — an unsold slot is the expected state before anyone buys it. Genuine
 * failures still throw, because a caller that cannot reach the chain should be
 * able to tell that apart from a slot that is simply empty.
 */
/**
 * The creative URI for a slot: which hook it points at, then what that hook
 * holds for it.
 *
 * Two single-value reads, and they are serial because the second needs the
 * first's answer — the hook is a property of the slot, not of this package.
 * `ad(slot)` would collapse them into one call at the cost of decoding the
 * protocol's whole `SlotInfo`, which is the trade this file refuses everywhere
 * a pinned bundle is doing the reading.
 */
export async function readAdURIForSlot(
  client: PublicClient,
  slotAddress: string,
  adHook?: string,
): Promise<string> {
  // Which hook does this slot point at? Asked of the SLOT, so a publisher
  // running their own AdLand deployment is read correctly with nothing passed,
  // and a slot pointing at some other hook is correctly read as having no ad
  // rather than being looked up on ours.
  const hook =
    (adHook as Address | undefined) ??
    (await client
      .readContract({
        address: slotAddress as Address,
        abi: SLOT_HOOK_ABI,
        functionName: "hook",
      })
      .catch(() => null));

  if (!hook || hook === ZERO_ADDRESS) return "";

  // A creative that will not read is no creative. That covers a hook which is
  // not an AdLand — a slot may point at any hook, and most of them have never
  // heard of a creative — and it is an empty ad space, not an error.
  return client
    .readContract({
      address: hook,
      abi: AD_HOOK_ABI,
      functionName: "creativeOf",
      args: [slotAddress as Address],
    })
    .catch(() => "");
}

export const fetchAd = async (
  client: PublicClient,
  slotAddress: string,
  gateway?: string,
  adHook?: string,
): Promise<AdSnapshot | null> => {
  const uri = await readAdURIForSlot(client, slotAddress, adHook);
  if (!uri) return null;

  try {
    return await fetchAdFromURI(uri, gateway);
  } catch (err) {
    if (err instanceof Error && err.message === AdDataQueryError.NO_AD) {
      return null;
    }
    throw err;
  }
};
